import React, { useMemo } from "react";

type PresenceHeatmapProps = {
    attendanceDates: string[];
    year?: number;
    colors?: [string, string, string, string, string];
    emptyColor?: string;
    cellSize?: number;
    gap?: number;
    showWeekdayLabels?: boolean;
    compact?: boolean;
    locale?: string;
};

type DayCell = {
    date: Date;
    iso: string;
    weekIndex: number;
    weekday: number;
    count: number;
    level: 0 | 1 | 2 | 3 | 4;
};

function toISO(d: Date) {
    return d.toISOString().slice(0, 10);
}

const monIndex = (utcDay: number) => (utcDay + 6) % 7;

function levelForCount(count: number, thresholds: number[]) {
    if (count <= 0) return 0;
    if (count <= thresholds[0]) return 1;
    if (count <= thresholds[1]) return 2;
    if (count <= thresholds[2]) return 3;
    return 4;
}

export default function PresenceHeatmap({
                                            attendanceDates,
                                            year = new Date().getFullYear(),
                                            colors = ["#b01add", "#b01add", "#b01add", "#b01add", "#b01add"],
                                            emptyColor = "#2d2d2d",
                                            cellSize = 12,
                                            gap = 2,
                                            showWeekdayLabels = true,
                                            compact = false,
                                            locale = "en",
                                        }: PresenceHeatmapProps) {
    const { cells, monthLabels, weeks } = useMemo(() => {
        const countMap = new Map<string, number>();
        for (const iso of attendanceDates) {
            countMap.set(iso, (countMap.get(iso) ?? 0) + 1);
        }

        const start = new Date(Date.UTC(year, 0, 1));
        const end = new Date(Date.UTC(year, 11, 31));

        const startMonday = new Date(start);
        startMonday.setUTCDate(start.getUTCDate() - monIndex(start.getUTCDay()));

        const endSunday = new Date(end);
        endSunday.setUTCDate(end.getUTCDate() + (6 - monIndex(end.getUTCDay())));

        const cells: DayCell[] = [];
        const monthLabels: { weekIndex: number; label: string }[] = [];

        let d = new Date(startMonday);
        let weekIndex = 0;
        let lastMonth = -1;

        while (d <= endSunday) {
            const weekday = monIndex(d.getUTCDay());
            const iso = toISO(d);
            const count = countMap.get(iso) ?? 0;

            cells.push({ date: new Date(d), iso, weekIndex, weekday, count, level: 0 });

            d.setUTCDate(d.getUTCDate() + 1);

            if (monIndex(d.getUTCDay()) === 0) {
                const monthOfNext = d.getUTCMonth();
                if (monthOfNext !== lastMonth && d.getUTCFullYear() === year) {
                    monthLabels.push({
                        weekIndex,
                        label: d.toLocaleString(locale, { month: "short", timeZone: "UTC" }),
                    });
                    lastMonth = monthOfNext;
                }
                weekIndex++;
            }
        }

        const positives = cells.map(c => c.count).filter(c => c > 0).sort((a, b) => a - b);
        const q = (p: number) => (positives.length ? positives[Math.floor((positives.length - 1) * p)] : 1);
        const thresholds = [q(0.25), q(0.5), q(0.75)];

        for (const c of cells) c.level = levelForCount(c.count, thresholds) as DayCell["level"];

        return { cells, monthLabels, weeks: weekIndex };
    }, [attendanceDates, year, locale]);

    const weekdayLabelsFull = new Array(7).fill(0).map((_, i) =>
        new Date(Date.UTC(2024, 0, 1 + i))
            .toLocaleDateString(locale, { weekday: "short", timeZone: "UTC" })
            .replace(/\.$/, "")
    );
    const weekdayLabelIndices = compact ? [0, 2, 4, 6] : [0, 1, 2, 3, 4, 5, 6];

    const labelColWidth = showWeekdayLabels ? (compact ? 14 : 26) : 6;
    const topPad = 22;
    const leftPad = labelColWidth;
    const width = leftPad + weeks * (cellSize + gap) - gap;
    const height = topPad + 7 * (cellSize + gap) - gap;

    const levelColor = (lvl: number) => (lvl === 0 ? emptyColor : colors[lvl - 1]);

    const monthLabelFilter = (i: number) => (compact ? i % 2 === 0 : true);

    return (
        <svg
            role="img"
            aria-label={`Presence heatmap for ${year}`}
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={{ display: "block", maxWidth: "100%" }}
        >
            {monthLabels.map((m, i) =>
                monthLabelFilter(i) ? (
                    <text
                        key={`m-${m.weekIndex}`}
                        x={leftPad + m.weekIndex * (cellSize + gap)}
                        y={12}
                        fontSize={compact ? 9 : 10}
                        fill="currentColor"
                        opacity="0.8"
                    >
                        {m.label}
                    </text>
                ) : null
            )}

            {showWeekdayLabels &&
                weekdayLabelIndices.map((idx) => (
                    <text
                        key={`w-${idx}`}
                        x={leftPad - 6}
                        y={topPad + idx * (cellSize + gap) + cellSize - 2}
                        fontSize={compact ? 9 : 10}
                        fill="currentColor"
                        opacity="0.7"
                        textAnchor="end"
                    >
                        {compact ? weekdayLabelsFull[idx].slice(0, 1) : weekdayLabelsFull[idx]}
                    </text>
                ))}

            {cells.map((c, i) => {
                const x = leftPad + c.weekIndex * (cellSize + gap);
                const y = topPad + c.weekday * (cellSize + gap);
                const title = `${c.date.toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                })}`;
                return (
                    <rect
                        key={i}
                        x={x}
                        y={y}
                        width={cellSize}
                        height={cellSize}
                        rx={2}
                        ry={2}
                        fill={levelColor(c.level)}
                    >
                        <title>{title}</title>
                    </rect>
                );
            })}
        </svg>
    );
}
